/**
 * Resolve a practical "last known location" for field employees:
 * 1) most recent punch with GPS (clock-out preferred, else clock-in)
 * 2) else property of their most recent completed job (primary assignee)
 */
import { supabase } from './supabase'
import { haversineKm, type LatLng } from './routeOptimizer'

export type EmployeeLocationSource = 'punch' | 'completed_job'

export type EmployeeLastLocation = LatLng & {
  employeeId: string
  source: EmployeeLocationSource
  at: string | null
}

function punchCoords(row: {
  clock_out_latitude?: number | null
  clock_out_longitude?: number | null
  clock_in_latitude?: number | null
  clock_in_longitude?: number | null
  clock_out_time?: string | null
  clock_in_time?: string | null
}): { coords: LatLng; at: string | null } | null {
  if (
    row.clock_out_latitude != null &&
    row.clock_out_longitude != null &&
    Number.isFinite(Number(row.clock_out_latitude)) &&
    Number.isFinite(Number(row.clock_out_longitude))
  ) {
    return {
      coords: {
        latitude: Number(row.clock_out_latitude),
        longitude: Number(row.clock_out_longitude),
      },
      at: row.clock_out_time || row.clock_in_time || null,
    }
  }
  if (
    row.clock_in_latitude != null &&
    row.clock_in_longitude != null &&
    Number.isFinite(Number(row.clock_in_latitude)) &&
    Number.isFinite(Number(row.clock_in_longitude))
  ) {
    return {
      coords: {
        latitude: Number(row.clock_in_latitude),
        longitude: Number(row.clock_in_longitude),
      },
      at: row.clock_in_time || null,
    }
  }
  return null
}

const LOOKBACK_DAYS = 7

export async function fetchEmployeeLastLocations(
  employeeIds: string[]
): Promise<Map<string, EmployeeLastLocation>> {
  const out = new Map<string, EmployeeLastLocation>()
  const ids = Array.from(new Set(employeeIds.filter(Boolean)))
  if (ids.length === 0) return out

  const since = new Date()
  since.setDate(since.getDate() - LOOKBACK_DAYS)
  const sinceIso = since.toISOString()

  const { data: punches } = await supabase
    .from('time_entries')
    .select(
      'employee_id, clock_in_time, clock_out_time, clock_in_latitude, clock_in_longitude, clock_out_latitude, clock_out_longitude'
    )
    .in('employee_id', ids)
    .gte('clock_in_time', sinceIso)
    .order('clock_in_time', { ascending: false })
    .limit(Math.min(ids.length * 20, 500))

  for (const row of punches || []) {
    const empId = row.employee_id as string
    if (!empId || out.has(empId)) continue
    const punched = punchCoords(row)
    if (!punched) continue
    out.set(empId, {
      employeeId: empId,
      ...punched.coords,
      source: 'punch',
      at: punched.at,
    })
  }

  const missing = ids.filter((id) => !out.has(id))
  if (missing.length === 0) return out

  const { data: completedJobs } = await supabase
    .from('jobs')
    .select(
      'id, assigned_employee_id, actual_end_time, scheduled_date, property:properties(latitude, longitude)'
    )
    .in('assigned_employee_id', missing)
    .eq('status', 'completed')
    .order('actual_end_time', { ascending: false })
    .limit(Math.min(missing.length * 10, 300))

  for (const row of completedJobs || []) {
    const empId = row.assigned_employee_id as string | null
    if (!empId || out.has(empId)) continue
    const prop = Array.isArray(row.property) ? row.property[0] : row.property
    const lat = prop?.latitude
    const lng = prop?.longitude
    if (
      lat == null ||
      lng == null ||
      !Number.isFinite(Number(lat)) ||
      !Number.isFinite(Number(lng))
    ) {
      continue
    }
    out.set(empId, {
      employeeId: empId,
      latitude: Number(lat),
      longitude: Number(lng),
      source: 'completed_job',
      at: row.actual_end_time || row.scheduled_date || null,
    })
  }

  return out
}

/**
 * Greedy assign: for each job (in given order), pick the closest employee
 * under max load. Returns jobId → employeeId with distance.
 */
export function assignJobsToClosestEmployees(args: {
  jobs: Array<{ id: string; latitude?: number | null; longitude?: number | null }>
  employees: Array<{ id: string; location: LatLng | null; currentLoad: number }>
  maxJobsPerEmployee?: number
}): Array<{ jobId: string; employeeId: string; distanceKm: number }> {
  const maxLoad = args.maxJobsPerEmployee ?? 12
  const loads = new Map(args.employees.map((e) => [e.id, e.currentLoad]))
  const results: Array<{ jobId: string; employeeId: string; distanceKm: number }> = []

  for (const job of args.jobs) {
    if (
      job.latitude == null ||
      job.longitude == null ||
      !Number.isFinite(job.latitude) ||
      !Number.isFinite(job.longitude)
    ) {
      continue
    }
    let best: { employeeId: string; distanceKm: number } | null = null
    for (const emp of args.employees) {
      if (!emp.location) continue
      const load = loads.get(emp.id) || 0
      if (load >= maxLoad) continue
      const d = haversineKm(
        emp.location.latitude,
        emp.location.longitude,
        job.latitude,
        job.longitude
      )
      if (!best || d < best.distanceKm) {
        best = { employeeId: emp.id, distanceKm: d }
      }
    }
    if (best) {
      results.push({
        jobId: job.id,
        employeeId: best.employeeId,
        distanceKm: best.distanceKm,
      })
      loads.set(best.employeeId, (loads.get(best.employeeId) || 0) + 1)
    }
  }
  return results
}
